/**
 * VoxelIntegration - Ties together VoxelWorld, terrain probes, and VoxelDebug
 * 
 * This module provides a single entry point for the voxel terrain system,
 * managing initialization, updates, and cleanup.
 * 
 * Implements the TerrainProbe interface (voxel-field queries) for spawn and build targeting.
 */

import * as THREE from 'three';
import { VoxelWorld } from './VoxelWorld.js';
import { VoxelDebugManager } from './VoxelDebug.js';
import { perfStats } from '../debug/PerformanceStats.js';
import type { TerrainProbe, GroundProbe } from '../spawn/TerrainProbe.js';
import { LAYER_SOLID } from './LayerConfig.js';
import {
  VoxelField, groundHeightAt, raycastVoxels, sampleSubmersion,
  RAY_SOLID, RAY_LIQUID, RAY_TRANSPARENT,
  CHUNK_WORLD_SIZE,
  PLAYER_HEIGHT_INNER, PLAYER_RADIUS, VOXEL_SCALE,
  type RaycastHit, type SubmersionResult,
} from '@worldify/shared';

/**
 * Configuration for the voxel integration.
 */
export interface VoxelConfig {
  /** Whether to enable debug visualization */
  debugEnabled?: boolean;
}

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: Required<VoxelConfig> = {
  debugEnabled: false,
};

/**
 * VoxelIntegration manages the voxel terrain system.
 * 
 * - Initializes VoxelWorld for chunk management
 * - Initializes VoxelDebug for visualization
 * - Implements TerrainProbe for the spawn system
 * - Provides voxel-field terrain probes (spawn, build targeting, punch)
 */
export class VoxelIntegration implements TerrainProbe {
  /** Shared voxel field used for terrain probes (assigned by GameCore). */
  private physicsField: VoxelField | null = null;
  private readonly groundOut: GroundProbe = { y: 0, material: 0, nx: 0, ny: 1, nz: 0 };
  private readonly hitOut: RaycastHit = { x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0, t: 0, material: 0, gx: 0, gy: 0, gz: 0 };

  /** The voxel world manager */
  readonly world: VoxelWorld;
  
  
  /** The debug visualization system */
  readonly debug: VoxelDebugManager;
  
  /** Configuration */
  private config: Required<VoxelConfig>;
  
  /** Whether the system has been initialized */
  private initialized = false;
  



  constructor(scene: THREE.Scene, config: VoxelConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    
    // Create subsystems
    this.world = new VoxelWorld(scene);
    this.debug = new VoxelDebugManager(scene);
  }

  /**
   * Initialize the voxel terrain system.
   * Creates initial chunks around the origin.
   */
  init(): void {
    if (this.initialized) return;
    
    // Initialize the world (generates initial chunks)
    this.world.init();


    
    // Update debug visualization if enabled
    if (this.config.debugEnabled) {
      this.debug.update(this.world.chunks, this.world.geometries, this.isChunkSelected);
    }
    
    this.initialized = true;
  }

  /** The LOD walk's verdict for a chunk — what the coord labels (F4) are filtered by. */
  private readonly isChunkSelected = (key: string): boolean =>
    this.world.terrain.isVisible(key);

  /**
   * Set the camera for visibility-based chunk loading.
   * Must be called before chunks will load in server mode.
   */
  setCamera(camera: THREE.Camera): void {
    this.world.setCamera(camera);
  }

  /**
   * Update the voxel system based on player position.
   * Should be called every frame.
   * 
   * @param playerPos Current player world position
   */
  update(playerPos: THREE.Vector3): void {
    if (!this.initialized) return;
    
    // Update world (handles chunk loading/unloading)
    this.world.update(playerPos);

    // Debug viz is inside the perf panel's `voxelUpdate` but OUTSIDE world.update, so without its own
    // term the overlay's cost was in no number a user can see — which is how a 16 ms/frame rebuild
    // passed for "the voxel system got slow".
    perfStats.begin('debugViz');
    this.debug.update(this.world.chunks, this.world.geometries, this.isChunkSelected);
    perfStats.end('debugViz');
  }


  /**
   * Enable or disable debug visualization.
   */
  setDebugEnabled(enabled: boolean): void {
    this.config.debugEnabled = enabled;
  }

  /**
   * Solid chunk meshes, for UI PICKING — not collision. Nothing here feeds physics; physics reads
   * the voxel field and must, because the field is what the player collides against.
   *
   * This exists for the explore-mode spawn marker, which has to hit whatever is actually DRAWN.
   * At explore zoom most of that is coarse far-LOD ring geometry kilometres out, and the physics
   * field is lod-0 only, so a field probe finds nothing there. COARSE CHUNKS ARE INCLUDED for
   * exactly that reason. Transparent foliage and liquid are excluded so a tap lands on ground
   * rather than a leaf or a lake surface.
   */
  getSolidMeshes(): THREE.Object3D[] {
    return this.world.terrain.pickables(LAYER_SOLID);
  }

  /**
   * Point terrain probes at the shared physics field (see physicsWorldAdapter). One field
   * instance is shared with the player controller so both answer from the same chunk cache
   * and there is a single invalidation point.
   */
  setPhysicsField(field: VoxelField): void {
    this.physicsField = field;
  }

  // ---- TerrainProbe ----

  /**
   * Vertical span of resident lod-0 terrain. LOD-0 ONLY, to match the physics field: a coarse
   * far-ring chunk covers 2^lod the world extent and would inflate the span with terrain the
   * field cannot answer for anyway.
   */
  loadedYExtent(): { minY: number; maxY: number } | null {
    let minY = Infinity;
    let maxY = -Infinity;
    for (const chunk of this.world.chunks.values()) {
      if (chunk.lod > 0) continue;
      const base = chunk.cy * CHUNK_WORLD_SIZE;
      if (base < minY) minY = base;
      if (base + CHUNK_WORLD_SIZE > maxY) maxY = base + CHUNK_WORLD_SIZE;
    }
    return minY === Infinity ? null : { minY, maxY };
  }

  /**
   * How far above and below the marker's raycast point to hunt for the real lod-0 surface. The marker
   * is placed by raycasting `getSolidMeshes()`, which deliberately INCLUDES coarse meshes so a tap
   * can land on far terrain — and a decimated surface sits at a different height from the lod-0 one.
   * The search therefore has to straddle the hit rather than start at it.
   */
  private static readonly GROUND_SEARCH_UP = 16;
  private static readonly GROUND_SEARCH_DOWN = 48;

  /**
   * IS THE lod-0 GROUND OF THIS COLUMN ACTUALLY BEING DRAWN? The Play button's visibility half.
   *
   * Asking `isChunkDisplayedAt(spawnPos)` directly does not work, and the arithmetic is the whole
   * bug: `spawnPos.y` is an EYE position, ground + PLAYER_HEIGHT + SPAWN_HEIGHT_OFFSET = +3.6 m,
   * while a chunk is 8 m tall. So for any column whose surface sits in the top 3.6 m of its chunk —
   * about 45% of them — the question landed on the EMPTY SKY CHUNK ABOVE, which has no geometry and
   * can never be "displayed". Deterministic per column, which is why nudging the marker fixed it
   * instantly and why waiting never did.
   *
   * The ground height comes from the PHYSICS FIELD (groundHeightAt), not from the marker's own hit
   * point: the field is lod-0 only by construction, so it cannot report a coarse height, and it
   * returns false over unknown space — which is exactly the answer wanted before lod 0 arrives.
   */
  isGroundDisplayedAt(x: number, z: number, nearY: number): boolean {
    const up = VoxelIntegration.GROUND_SEARCH_UP;
    const ground = this.probeDown(
      x, z, nearY + up, up + VoxelIntegration.GROUND_SEARCH_DOWN, true,
    );
    if (!ground) return false;
    // One voxel INTO the ground, so a surface sitting exactly on a chunk boundary resolves to the
    // solid chunk below it rather than the air chunk above — same nudge isSpawnReadyAt uses.
    return this.world.isChunkDisplayedAt(x, ground.y - VOXEL_SCALE, z, 0);
  }

  probeDown(x: number, z: number, yStart: number, maxDown: number, solidOnly: boolean): GroundProbe | null {
    if (!this.physicsField) return null;
    if (solidOnly) {
      // groundHeightAt uses the same collidable field the player stands on, so a spawn can
      // never land on water or foliage the way the old all-layers mesh raycast could.
      if (!groundHeightAt(this.physicsField, x, z, yStart, maxDown, this.groundOut)) return null;
      return this.groundOut;
    }
    if (!raycastVoxels(this.physicsField, x, yStart, z, 0, -1, 0, maxDown,
      RAY_SOLID | RAY_LIQUID | RAY_TRANSPARENT, this.hitOut)) return null;
    this.groundOut.y = this.hitOut.y;
    this.groundOut.material = this.hitOut.material;
    this.groundOut.nx = this.hitOut.nx;
    this.groundOut.ny = this.hitOut.ny;
    this.groundOut.nz = this.hitOut.nz;
    return this.groundOut;
  }

  probeRay(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    maxDist: number,
    mask: number,
    out: RaycastHit
  ): boolean {
    if (!this.physicsField) return false;
    return raycastVoxels(this.physicsField, ox, oy, oz, dx, dy, dz, maxDist, mask, out);
  }

  /**
   * Capsule submersion at an EYE position (`PlayerLocal.position` is the eye — see its doc).
   * Reads the liquid field, where UNKNOWN is not liquid, so a streaming hole never reads as water.
   * @returns false when there is no field yet (`out` untouched).
   */
  probeSubmersion(x: number, eyeY: number, z: number, out: SubmersionResult): boolean {
    if (!this.physicsField) return false;
    sampleSubmersion(this.physicsField, x, eyeY, z, PLAYER_HEIGHT_INNER, PLAYER_RADIUS, out);
    return true;
  }

  /**
   * Get statistics about the voxel system.
   */
  getStats() {
    const worldStats = this.world.getStats();
    return {
      ...worldStats,
      debugEnabled: this.config.debugEnabled,
    };
  }

  /**
   * Check if the system is initialized.
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Force refresh of all chunks.
   */
  refresh(): void {
    this.world.refresh();
  }

  /**
   * Clear all chunks and reload from server.
   * Used for dev/debug to force fresh chunk generation.
   * @param playerPos Current player position (to reload chunks around)
   */
  clearAndReload(playerPos?: THREE.Vector3): void {

    // Clear and reload world
    this.world.clearAndReload(playerPos);
    
    // Debug will auto-update on next frame
  }

  /**
   * Dispose of all resources.
   */
  dispose(): void {
    this.debug.dispose();
    this.world.dispose();
    this.initialized = false;
  }
}

/**
 * Create a VoxelIntegration instance.
 * Convenience function for creating and initializing the voxel system.
 * 
 * @param scene The Three.js scene
 * @param config Optional configuration
 * @returns Initialized VoxelIntegration
 */
export function createVoxelIntegration(scene: THREE.Scene, config?: VoxelConfig): VoxelIntegration {
  const integration = new VoxelIntegration(scene, config);
  integration.init();
  return integration;
}
